import type { ApolloError } from '@apollo/client';
import { AlertTriangleIcon, RotateCcwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

export type ErrorFallbackProps = {
  error: Error | ApolloError;
  reset: () => void;
};

export function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  const { t } = useTranslation();

  const commonError = t('common.error');
  const genericMessage = t('common.sorryThereWasAnError');
  const message = error?.message?.trim() || genericMessage;
  // operationName added by the error link
  const operationName = (error as ApolloError)?.graphQLErrors?.[0]?.extensions
    ?.operationName as string | undefined;

  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 p-4"
      data-testid="error-boundary-fallback"
    >
      <div className="border-destructive/40 bg-destructive/10 text-destructive flex w-full items-start gap-2 rounded-lg border p-3 text-sm">
        <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
        <span>
          {operationName ? (
            <>
              {commonError} <b>{operationName}:</b> {message}
            </>
          ) : (
            `${commonError} ${message}`
          )}
        </span>
      </div>
      <Button variant="outline" size="lg" onClick={reset}>
        <RotateCcwIcon />
        {t('common.retry')}
      </Button>
    </div>
  );
}
